'use client'

import React from 'react'
import { CTAButton as CTAButtonComponent, type CTAButtonVariant } from '@/components/ui/cta-button'
import { resolveHref } from '@/utilities/resolveHref'
import { mapLegacyAppearance, type LinkFieldType } from '@/fields/link'
import type { HeroBackgroundColor } from '@/heros/types'

interface CTAButtonProps {
  link: LinkFieldType
  backgroundColor?: HeroBackgroundColor | null
}

/**
 * Maps CMS appearance to button variant, handling legacy values and background-based adjustments.
 */
const getVariant = (
  appearance: LinkFieldType['appearance'],
  heroBackground?: HeroBackgroundColor | null,
): CTAButtonVariant => {
  // Map any legacy values to new values
  let variant = mapLegacyAppearance(appearance)

  // Adjust for dark backgrounds
  if (heroBackground === 'black') {
    // Switch outline buttons to white outline on black backgrounds
    if (variant === 'blackOutline') {
      variant = 'whiteOutline'
    }
  }

  return variant
}

export const CTAButton: React.FC<CTAButtonProps> = ({ link, backgroundColor }) => {
  // Use existing utility to resolve href from link data
  const href = resolveHref(link)

  // Get the appropriate variant
  const variant = getVariant(link.appearance, backgroundColor)

  return (
    <CTAButtonComponent
      variant={variant}
      href={href}
      newTab={link.newTab || false}
      surface={
        backgroundColor === 'black' || backgroundColor === 'lime' ? backgroundColor : undefined
      }
    >
      {link.label}
    </CTAButtonComponent>
  )
}
